import { useState, useEffect, useCallback } from 'react';
import {
  Clock,
  Check,
  X,
  ExternalLink,
  Users,
  Mail,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/i18n';
import { useToast } from '@/hooks/use-toast';
import { formatDateTime } from '@/lib/utils';
import { getSubmitHistory, type AppRequestData, type ListAppsParams } from '@/api/apps';

export default function HistoryPage() {
  const { t } = useI18n();
  const { toast } = useToast();

  // Data states
  const [requests, setRequests] = useState<AppRequestData[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const params: ListAppsParams = {
        page,
        pageSize,
      };
      const result = await getSubmitHistory(params);
      setRequests(result.items ?? []);
      setTotal(result.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load history';
      toast({
        title: t.common.error,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, toast, t.common.error]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <Check className="w-4 h-4 text-green-600" />;
      case 'rejected':
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  if (loading && requests.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t.creator.submissionHistory}</h1>
        <p className="text-gray-500 mt-1">{t.creator.submissionHistoryDesc}</p>
      </div>

      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">{t.creator.noHistory}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {requests.map((request) => (
              <Card key={request.id}>
                <CardContent className="py-5">
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        request.status === 'approved'
                          ? 'bg-green-100'
                          : request.status === 'rejected'
                          ? 'bg-red-100'
                          : 'bg-yellow-100'
                      }`}
                    >
                      {getStatusIcon(request.status)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">{request.appName}</h3>
                        <Badge
                          variant={
                            request.type === 'create'
                              ? 'info'
                              : request.type === 'update'
                              ? 'warning'
                              : 'danger'
                          }
                        >
                          {t.requestTypes[request.type as keyof typeof t.requestTypes]}
                        </Badge>
                        <Badge
                          variant={
                            request.status === 'approved'
                              ? 'success'
                              : request.status === 'rejected'
                              ? 'danger'
                              : 'warning'
                          }
                        >
                          {t.status[request.status as keyof typeof t.status]}
                        </Badge>
                      </div>

                      {request.appDescription && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {request.appDescription}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {t.creator.submittedAt} {formatDateTime(request.createdAt)}
                        </span>
                        {request.appLink && (
                          <a
                            href={request.appLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary-600 hover:underline"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {t.admin.viewApp}
                          </a>
                        )}
                      </div>

                      {/* Permissions info */}
                      {(request.allowedGroups?.length || request.allowedEmails?.length) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {request.allowedGroups?.map((group) => (
                            <span
                              key={group}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded-full"
                            >
                              <Users className="w-3 h-3" />
                              {group}
                            </span>
                          ))}
                          {request.allowedEmails?.slice(0, 3).map((email) => (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded-full"
                            >
                              <Mail className="w-3 h-3" />
                              {email}
                            </span>
                          ))}
                          {(request.allowedEmails?.length || 0) > 3 && (
                            <span className="text-xs text-gray-500">
                              +{(request.allowedEmails?.length || 0) - 3} {t.common.more}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Review note */}
                      {request.reviewNote && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">{t.creator.reviewNoteLabel}</span>
                            {request.reviewNote}
                          </p>
                          {request.reviewedAt && (
                            <p className="text-xs text-gray-400 mt-1">
                              {t.creator.reviewedAt} {formatDateTime(request.reviewedAt)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-gray-500">
                {t.admin.showingReviews
                  .replace('{from}', String((page - 1) * pageSize + 1))
                  .replace('{to}', String(Math.min(page * pageSize, total)))
                  .replace('{total}', String(total))}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-gray-700">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
